import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { FaRegBell } from 'react-icons/fa6';
import { FiCalendar, FiCheckCircle, FiFilter, FiSearch, FiStar } from 'react-icons/fi';

import { HeaderInput } from '@/components/Header/HeaderInputs';
import { SidePanel } from '@/components/SidePanel';
import { SidePanelHeader } from '@/components/SidePanelHeader';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useInfiniteTrigger } from '@/hooks/useInfiniteTrigger';
import { useNotificationsPanel } from '@/hooks/useNotificationsPanel';
import { useRoles } from '@/hooks/useRoles';
import { FRONTEND_FEATURES } from '@/permissions/frontend-features';
import { useFrontendFeature } from '@/permissions/useFrontendFeature';
import { useChats } from '@/store/chats/chats.store';
import { NotificationSubcategory } from '@/store/notifications/notifications.dto';
import {
  useNotificationsQuery,
  useReadAllNotificationsMutation,
  useReadNotificationsMutation,
  useUnreadNotificationsMutation,
} from '@/store/notifications/notifications.store';
import { isInOwnerGroup } from '@/utils/roles';

import { batchByTitle, formatDateDivider, getDateFrom, toPanelItem } from './notification-display';
import { NotificationCard } from './NotificationCard';
import styles from './NotificationsSidePanel.module.scss';
import type {
  NotificationBatch,
  NotificationDateFilter,
  NotificationItem,
  NotificationSubcategoryFilter,
} from './types';

const PAGE_SIZE = 200;
const PRIORITY_ROLE = 'owner';

const SUBCATEGORY_OPTIONS: { value: NotificationSubcategoryFilter; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: NotificationSubcategory.MESSAGES, label: 'Messages' },
  { value: NotificationSubcategory.MENTIONS, label: 'Mentions' },
  { value: NotificationSubcategory.MEMBERSHIP, label: 'Membership' },
  { value: NotificationSubcategory.TASKS, label: 'Tasks' },
  { value: NotificationSubcategory.EVENTS, label: 'Events' },
  { value: NotificationSubcategory.MENU, label: 'Menu' },
  { value: NotificationSubcategory.PAYMENTS, label: 'Payments' },
  { value: NotificationSubcategory.SUBSCRIPTIONS, label: 'Subscriptions' },
  { value: NotificationSubcategory.ROLES, label: 'Roles' },
  { value: NotificationSubcategory.INVITES, label: 'Invites' },
  { value: NotificationSubcategory.REFERRALS, label: 'Referrals' },
  { value: NotificationSubcategory.OWNERSHIP, label: 'Ownership' },
  { value: NotificationSubcategory.GENERAL, label: 'General' },
];

const DATE_OPTIONS: { value: NotificationDateFilter; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
];

type MenuKey = 'subcategory' | 'date';

interface DatedGroup {
  date: string;
  batches: NotificationBatch[];
}

const groupAndBatch = (items: NotificationItem[]): DatedGroup[] => {
  const byDate: { date: string; items: NotificationItem[] }[] = [];
  for (const item of items) {
    const last = byDate[byDate.length - 1];
    if (last?.date === item.dateKey) last.items.push(item);
    else byDate.push({ date: item.dateKey, items: [item] });
  }
  return byDate.map((g) => ({ date: g.date, batches: batchByTitle(g.items) }));
};

const NotificationsSidePanel = () => {
  const { isOpen, close } = useNotificationsPanel();
  const { userRoles } = useRoles();
  const isOwner = isInOwnerGroup(userRoles);

  const chatRoomsLoaded = useChats((s) => s.chatRooms !== null);
  const getChatRooms = useChats((s) => s.getChatRooms);
  useEffect(() => {
    if (isOpen && !chatRoomsLoaded) void getChatRooms();
  }, [isOpen, chatRoomsLoaded, getChatRooms]);

  // Cards stay mounted while the panel is closed, so their invite checks never refetch on
  // their own — and a withdrawn invite notifies the invitee of nothing. Recheck on open,
  // or Accept outlives the invite it answers.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (isOpen) void queryClient.invalidateQueries({ queryKey: ['invites'] });
  }, [isOpen, queryClient]);

  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [subcategoryFilter, setSubcategoryFilter] = useState<NotificationSubcategoryFilter>('all');
  const canPay = useFrontendFeature(FRONTEND_FEATURES.PaymentsUI);
  const subcategoryOptions = useMemo(
    () =>
      canPay
        ? SUBCATEGORY_OPTIONS
        : SUBCATEGORY_OPTIONS.filter((o) => o.value !== NotificationSubcategory.PAYMENTS),
    [canPay],
  );
  const [dateFilter, setDateFilter] = useState<NotificationDateFilter>('all');
  const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);

  const queryFilters = useMemo(() => {
    const date_from = getDateFrom(dateFilter);
    return {
      ...(!isOwner && { role_priority: PRIORITY_ROLE }),
      ...(unreadOnly && { only_unread: true }),
      ...(date_from && { date_from }),
      ...(subcategoryFilter !== 'all' && { subcategory: subcategoryFilter }),
    };
  }, [isOwner, unreadOnly, dateFilter, subcategoryFilter]);

  // Off while closed: no 200-item fetch on app load or per WS event. Re-enabling
  // refetches the stale cache, so every open shows fresh data; WS keeps it live while open.
  const { data, fetchNextPage, hasNextPage, isFetching, isSuccess } = useNotificationsQuery(
    PAGE_SIZE,
    queryFilters,
    isOpen,
  );
  const { mutate: markAsRead } = useReadNotificationsMutation();
  const { mutate: markAsUnread } = useUnreadNotificationsMutation();
  const { mutate: markAllAsRead } = useReadAllNotificationsMutation();

  const triggerRef = useInfiniteTrigger({
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetching,
    options: { rootMargin: '200px' },
  });

  const unreadCount = data?.pages[0]?.unread ?? 0;

  const items = useMemo<NotificationItem[]>(() => {
    if (!data) return [];
    const result: NotificationItem[] = [];
    for (const page of data.pages) {
      for (const n of page.items) result.push(toPanelItem(n));
    }
    return result;
  }, [data]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((n) => `${n.title} ${n.description}`.toLowerCase().includes(query));
  }, [items, search]);

  const { priorityBatches, datedGroups } = useMemo(() => {
    if (isOwner) {
      return {
        priorityBatches: [] as NotificationBatch[],
        datedGroups: groupAndBatch(filtered),
      };
    }
    const priority: NotificationItem[] = [];
    const regular: NotificationItem[] = [];
    for (const item of filtered) {
      if (item.senderRole === PRIORITY_ROLE) priority.push(item);
      else regular.push(item);
    }
    return {
      priorityBatches: batchByTitle(priority),
      datedGroups: groupAndBatch(regular),
    };
  }, [filtered, isOwner]);

  const handleMarkRead = useCallback(
    (ids: string[]) => {
      if (ids.length) markAsRead({ notification_ids: ids });
    },
    [markAsRead],
  );

  const handleMarkUnread = useCallback(
    (ids: string[]) => {
      if (ids.length) markAsUnread({ notification_ids: ids });
    },
    [markAsUnread],
  );

  const openMenu = (menu: MenuKey) => setActiveMenu((current) => (current === menu ? null : menu));
  const closeMenu = () => setActiveMenu(null);

  return (
    <SidePanel open={isOpen} onClose={close}>
      <div className={styles.panel}>
        <header className={styles.header}>
          <SidePanelHeader
            title="Notifications"
            icon={<FaRegBell />}
            onClose={close}
            trailing={unreadCount > 0 ? <span className={styles.badge}>{unreadCount}</span> : null}
          />
        </header>

        <div className={styles.toolbar}>
          <HeaderInput
            icon={<FiSearch />}
            placeholder="Search notifications"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className={styles.actions}>
            <button
              type="button"
              className={clsx(styles.chip, unreadOnly && styles.chipOn)}
              onClick={() => setUnreadOnly((v) => !v)}
            >
              <span className={styles.chipDot} />
              Unread
            </button>

            <FilterDropdown
              icon={<FiFilter />}
              options={subcategoryOptions}
              value={subcategoryFilter}
              isOpen={activeMenu === 'subcategory'}
              onToggle={() => openMenu('subcategory')}
              onClose={closeMenu}
              onChange={setSubcategoryFilter}
            />

            <FilterDropdown
              icon={<FiCalendar />}
              options={DATE_OPTIONS}
              value={dateFilter}
              isOpen={activeMenu === 'date'}
              onToggle={() => openMenu('date')}
              onClose={closeMenu}
              onChange={setDateFilter}
            />

            <div className={styles.spacer} />

            <button
              type="button"
              className={styles.markAllButton}
              onClick={() => markAllAsRead()}
              disabled={unreadCount === 0}
            >
              <FiCheckCircle />
              Mark All as Read
            </button>
          </div>
        </div>

        <div className={styles.feed}>
          {priorityBatches.length > 0 && (
            <>
              <div className={styles.priorityLabel}>
                <FiStar />
                <span>Priority</span>
              </div>
              <section className={styles.section}>
                {priorityBatches.map((batch) => (
                  <NotificationCard
                    key={batch.key}
                    batch={batch}
                    onMarkRead={handleMarkRead}
                    onMarkUnread={handleMarkUnread}
                    pinned
                  />
                ))}
              </section>
            </>
          )}

          {datedGroups.map((group) => (
            <div key={group.date}>
              <div className={styles.dateDivider}>{formatDateDivider(group.date)}</div>
              <section className={styles.section}>
                {group.batches.map((batch) => (
                  <NotificationCard
                    key={batch.key}
                    batch={batch}
                    onMarkRead={handleMarkRead}
                    onMarkUnread={handleMarkUnread}
                  />
                ))}
              </section>
            </div>
          ))}

          <div ref={triggerRef} />

          {isSuccess && filtered.length === 0 && (
            <div className={styles.empty}>
              <FaRegBell />
              <span>
                {items.length === 0
                  ? 'You’re all caught up.'
                  : 'No notifications match. Please try widening the filters.'}
              </span>
            </div>
          )}
        </div>
      </div>
    </SidePanel>
  );
};

interface FilterDropdownProps<T extends string> {
  icon: ReactNode;
  options: { value: T; label: string }[];
  value: T;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (value: T) => void;
}

const FilterDropdown = <T extends string>({
  icon,
  options,
  value,
  isOpen,
  onToggle,
  onClose,
  onChange,
}: FilterDropdownProps<T>) => {
  const ref = useClickOutside<HTMLDivElement>(() => {
    if (isOpen) onClose();
  });
  const selected = options.find((o) => o.value === value) ?? options[0];
  const isActive = value !== options[0].value;

  return (
    <div className={styles.dropdown} ref={ref}>
      <button
        type="button"
        className={clsx(styles.chip, isActive && styles.chipOn)}
        onClick={onToggle}
      >
        {icon}
        <span>{selected.label}</span>
        <BiChevronDown className={styles.chipCaret} />
      </button>
      {isOpen && (
        <div className={styles.menu}>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={clsx(styles.menuItem, value === opt.value && styles.menuItemActive)}
              onClick={() => {
                onChange(opt.value);
                onClose();
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsSidePanel;
